import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  IndianRupee, ArrowLeft, Upload, Loader2, Plus, Trash2, Menu, RefreshCw,
  CheckCircle2, AlertTriangle, FileText, Building2, Users, Handshake, Save, X, History,
  TrendingUp, CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { authGet, authPost, authPatch, authDelete, authUpload, isAdvisor } from "@/utils/auth";
import MobileBottomNav from "@/components/MobileBottomNav";
import MobileSidebar from "@/components/MobileSidebar";
import RevenueAnalytics from "@/components/revenue/RevenueAnalytics";

const inr = (n) => (n === null || n === undefined || n === "" ? "—" : "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 }));
const CHANNEL_TYPES = ["Broker platform", "Direct insurer", "Insurance marketing firm", "Associated firm"];
const SOURCES = ["Direct", "Beshak", "Associate"];
const STATUSES = ["CLEAN", "IN_QUESTION", "SETTLED_NON_CASH"];
const ENTRY_TYPES = ["Fresh Year 1", "Multi-year Year N payout", "Renewal", "Contest"];

const statusColor = {
  new: "bg-emerald-100 text-emerald-700 border-emerald-300",
  updated: "bg-amber-100 text-amber-700 border-amber-300",
  unchanged: "bg-slate-100 text-slate-600 border-slate-300",
  failed: "bg-red-100 text-red-700 border-red-300",
};

const RevenueDashboard = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tab, setTab] = useState("dashboard");
  const [summary, setSummary] = useState(null);

  // records
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [filters, setFilters] = useState({ search: "", channel: "all", source: "all", status: "all", entry_type: "all", referral_paid: "all" });
  const [selected, setSelected] = useState(new Set());
  const [bulk, setBulk] = useState({ source: "", associate: "", referral_share_pct: "" });

  // masters
  const [channels, setChannels] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [imports, setImports] = useState([]);

  // projections (Phase 2)
  const [projections, setProjections] = useState([]);
  const [projSummary, setProjSummary] = useState(null);
  const [loadingProj, setLoadingProj] = useState(false);
  const [projFilters, setProjFilters] = useState({ certainty: "all", channel: "all", overdue: "all" });

  // import
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [channelMap, setChannelMap] = useState({});

  useEffect(() => {
    if (!isAdvisor()) {
      toast.error("Admin access only");
      navigate("/advisor/dashboard");
      return;
    }
    loadAll();
  }, []);

  const loadSummary = useCallback(async () => {
    const r = await authGet("/api/advisor/revenue/summary");
    if (r.ok) setSummary(r.data);
  }, []);

  const loadRecords = useCallback(async () => {
    setLoadingRecords(true);
    const qs = Object.entries(filters)
      .filter(([, v]) => v && v !== "all")
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    const r = await authGet(`/api/advisor/revenue/records${qs ? "?" + qs : ""}`);
    if (r.ok) setRecords(r.data.records || []);
    setLoadingRecords(false);
  }, [filters]);

  const loadMasters = useCallback(async () => {
    const [c, a, im] = await Promise.all([
      authGet("/api/advisor/revenue/channels"),
      authGet("/api/advisor/revenue/associates"),
      authGet("/api/advisor/revenue/imports"),
    ]);
    if (c.ok) setChannels(c.data.channels || []);
    if (a.ok) setAssociates(a.data.associates || []);
    if (im.ok) setImports(im.data.imports || []);
  }, []);

  const loadAll = async () => { await Promise.all([loadSummary(), loadRecords(), loadMasters()]); };

  useEffect(() => { loadRecords(); }, [filters]);

  const loadProjections = useCallback(async () => {
    setLoadingProj(true);
    const qs = Object.entries(projFilters)
      .filter(([, v]) => v && v !== "all")
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    const r = await authGet(`/api/advisor/revenue/projections${qs ? "?" + qs : ""}`);
    if (r.ok) { setProjections(r.data.projections || []); setProjSummary(r.data); }
    setLoadingProj(false);
  }, [projFilters]);

  useEffect(() => { if (tab === "projections") loadProjections(); }, [tab, loadProjections]);

  // ---------- inline record edit ----------
  const patchRecord = async (id, fields) => {
    setRecords((prev) => prev.map((r) => (r.id === id ? { ...r, ...fields } : r)));
    const r = await authPatch(`/api/advisor/revenue/records/${id}`, fields);
    if (r.ok) {
      setRecords((prev) => prev.map((x) => (x.id === id ? r.data.record : x)));
      loadSummary();
    } else toast.error(r.data?.detail || "Update failed");
  };

  const deleteRecord = async (id) => {
    if (!window.confirm("Delete this revenue record?")) return;
    const r = await authDelete(`/api/advisor/revenue/records/${id}`);
    if (r.ok) { setRecords((prev) => prev.filter((x) => x.id !== id)); loadSummary(); toast.success("Deleted"); }
  };

  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelected((prev) => (prev.size === records.length ? new Set() : new Set(records.map((r) => r.id))));

  const applyBulk = async () => {
    const fields = {};
    if (bulk.source) fields.source = bulk.source;
    if (bulk.associate) fields.associate = bulk.associate;
    if (bulk.referral_share_pct !== "") fields.referral_share_pct = Number(bulk.referral_share_pct);
    if (Object.keys(fields).length === 0) { toast.error("Choose a field to set"); return; }
    const r = await authPost("/api/advisor/revenue/records/bulk-update", { ids: [...selected], fields });
    if (r.ok) { toast.success(`Updated ${r.data.updated} records`); setSelected(new Set()); setBulk({ source: "", associate: "", referral_share_pct: "" }); loadRecords(); loadSummary(); }
    else toast.error("Bulk update failed");
  };

  // ---------- import ----------
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setPreview(null); setChannelMap({});
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await authUpload("/api/advisor/revenue/import/preview", fd);
      if (r.ok) {
        setPreview(r.data);
        const cm = {}; (r.data.unknown_channels || []).forEach((u) => { cm[u] = ""; });
        setChannelMap(cm);
        toast.success(`Parsed ${r.data.total} rows (${r.data.method === "ai" ? "AI" : "CSV"})`);
      } else toast.error(r.data?.detail || "Could not read file");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const commitImport = async () => {
    if (!preview) return;
    const unknowns = preview.unknown_channels || [];
    const create_channels = []; const channel_map = {};
    for (const u of unknowns) {
      const sel = channelMap[u];
      if (!sel) { toast.error(`Map or create channel "${u}" first`); return; }
      if (sel.startsWith("__create__")) { create_channels.push({ name: u, type: sel.split("::")[1] || "Broker platform" }); channel_map[u] = u; }
      else channel_map[u] = sel;
    }
    setCommitting(true);
    const r = await authPost("/api/advisor/revenue/import/commit", { token: preview.token, channel_map, create_channels });
    setCommitting(false);
    if (r.ok) {
      const res = r.data.results;
      toast.success(`Imported — ${res.new} new, ${res.updated} updated, ${res.unchanged} unchanged${res.skipped ? `, ${res.skipped} skipped` : ""}`);
      setPreview(null); setTab("records"); loadAll();
    } else toast.error(r.data?.detail || "Commit failed");
  };

  // ---------- channel / associate CRUD ----------
  const [chanForm, setChanForm] = useState(null);
  const saveChannel = async () => {
    if (!chanForm.name?.trim()) { toast.error("Name required"); return; }
    const payload = { name: chanForm.name, type: chanForm.type, payout_entity: chanForm.payout_entity, default_renewal_rate_pct: Number(chanForm.default_renewal_rate_pct) || 7.5, default_renewal_probability: Number(chanForm.default_renewal_probability) || 70 };
    const r = chanForm.id ? await authPatch(`/api/advisor/revenue/channels/${chanForm.id}`, payload) : await authPost("/api/advisor/revenue/channels", payload);
    if (r.ok) { toast.success("Saved"); setChanForm(null); loadMasters(); } else toast.error(r.data?.detail || "Save failed");
  };
  const deleteChannel = async (id) => { if (!window.confirm("Delete channel?")) return; await authDelete(`/api/advisor/revenue/channels/${id}`); loadMasters(); };

  const [assocForm, setAssocForm] = useState(null);
  const saveAssociate = async () => {
    if (!assocForm.name?.trim()) { toast.error("Name required"); return; }
    const payload = { name: assocForm.name, default_share_pct: Number(assocForm.default_share_pct) || 0, contact: assocForm.contact };
    const r = assocForm.id ? await authPatch(`/api/advisor/revenue/associates/${assocForm.id}`, payload) : await authPost("/api/advisor/revenue/associates", payload);
    if (r.ok) { toast.success("Saved"); setAssocForm(null); loadMasters(); } else toast.error(r.data?.detail || "Save failed");
  };
  const deleteAssociate = async (id) => { if (!window.confirm("Delete associate?")) return; await authDelete(`/api/advisor/revenue/associates/${id}`); loadMasters(); };

  const cardCls = "bg-white/60 border border-[#C9D4E6] rounded-2xl";

  return (
    <div className="min-h-screen bg-[#EDF1F8] pb-24 md:pb-8" data-testid="revenue-dashboard">
      <MobileSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur border-b border-[#C9D4E6] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/advisor/dashboard")} className="text-[#3A4557] hover:text-[#1B2A4A]" data-testid="revenue-back-btn"><ArrowLeft className="h-5 w-5" /></button>
          <div className="h-9 w-9 rounded-xl bg-emerald-500/15 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-emerald-600" /></div>
          <div className="flex-1">
            <h1 className="text-[#1B2A4A] font-bold text-lg leading-tight">Revenue</h1>
            <p className="text-[#3A4557] text-xs">Commission tracking, referrals & net revenue</p>
          </div>
          <button onClick={() => loadAll()} className="text-[#3A4557] hover:text-[#1B2A4A] p-2" data-testid="revenue-refresh-btn"><RefreshCw className="h-4 w-4" /></button>
          <button onClick={() => setSidebarOpen(true)} className="md:hidden text-[#3A4557] p-2"><Menu className="h-5 w-5" /></button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-5 space-y-5">
        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: "Gross Commission", val: summary?.gross_commission, color: "text-[#1B2A4A]", tid: "sum-gross" },
            { label: "Referral Payable", val: summary?.referral_payable, color: "text-amber-600", tid: "sum-payable" },
            { label: "Net Revenue", val: summary?.net_revenue, color: "text-emerald-600", tid: "sum-net" },
            { label: "Expected Future", val: summary?.expected_future, color: "text-blue-700", tid: "sum-future" },
            { label: "Records", val: summary?.record_count, color: "text-[#1B2A4A]", isCount: true, tid: "sum-count" },
          ].map((c) => (
            <div key={c.label} className={`${cardCls} p-4`} data-testid={c.tid}>
              <p className="text-[#3A4557] text-xs">{c.label}</p>
              <p className={`text-xl font-bold mt-1 ${c.color}`}>{c.isCount ? (c.val ?? 0) : inr(c.val)}</p>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <div className="overflow-x-auto -mx-1 px-1">
            <TabsList className="bg-white/70 border border-[#C9D4E6] flex-nowrap w-max">
              <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
              <TabsTrigger value="records" data-testid="tab-records">Records</TabsTrigger>
              <TabsTrigger value="projections" data-testid="tab-projections">Future income</TabsTrigger>
              <TabsTrigger value="import" data-testid="tab-import">Import</TabsTrigger>
              <TabsTrigger value="channels" data-testid="tab-channels">Channels</TabsTrigger>
              <TabsTrigger value="associates" data-testid="tab-associates">Associates</TabsTrigger>
            </TabsList>
          </div>

          {/* ---------------- DASHBOARD (analytics & reports) ---------------- */}
          <TabsContent value="dashboard" className="space-y-3">
            <RevenueAnalytics channels={channels} />
          </TabsContent>

          {/* ---------------- RECORDS ---------------- */}
          <TabsContent value="records" className="space-y-3">
            <div className="flex flex-wrap gap-2 items-center">
              <Input placeholder="Search client / policy / insurer…" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="max-w-xs bg-white" data-testid="rev-search" />
              <FilterSelect label="Channel" value={filters.channel} onChange={(v) => setFilters((f) => ({ ...f, channel: v }))} options={channels.map((c) => c.name)} tid="filter-channel" />
              <FilterSelect label="Source" value={filters.source} onChange={(v) => setFilters((f) => ({ ...f, source: v }))} options={SOURCES} tid="filter-source" />
              <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={STATUSES} tid="filter-status" />
              <FilterSelect label="Entry" value={filters.entry_type} onChange={(v) => setFilters((f) => ({ ...f, entry_type: v }))} options={ENTRY_TYPES} tid="filter-entry" />
              <FilterSelect label="Referral" value={filters.referral_paid} onChange={(v) => setFilters((f) => ({ ...f, referral_paid: v }))} options={[{ v: "true", l: "Paid" }, { v: "false", l: "Unpaid" }]} tid="filter-referral" />
            </div>

            {selected.size > 0 && (
              <div className={`${cardCls} p-3 flex flex-wrap items-end gap-2`} data-testid="bulk-bar">
                <span className="text-[#1B2A4A] text-sm font-medium">{selected.size} selected · set:</span>
                <FilterSelect label="Source" value={bulk.source || "all"} onChange={(v) => setBulk((b) => ({ ...b, source: v === "all" ? "" : v }))} options={SOURCES} tid="bulk-source" />
                <Select value={bulk.associate || "none"} onValueChange={(v) => setBulk((b) => ({ ...b, associate: v === "none" ? "" : v }))}>
                  <SelectTrigger className="w-40 bg-white h-9" data-testid="bulk-associate"><SelectValue placeholder="Associate" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Associate —</SelectItem>{associates.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" placeholder="Share %" value={bulk.referral_share_pct} onChange={(e) => setBulk((b) => ({ ...b, referral_share_pct: e.target.value }))} className="w-24 bg-white h-9" data-testid="bulk-share" />
                <Button onClick={applyBulk} className="bg-[#A87608] hover:bg-[#8f6307] text-white h-9" data-testid="bulk-apply">Apply</Button>
                <Button variant="ghost" onClick={() => setSelected(new Set())} className="h-9">Clear</Button>
              </div>
            )}

            <div className={`${cardCls} overflow-x-auto`}>
              {loadingRecords ? (
                <div className="p-10 text-center text-[#3A4557]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
              ) : records.length === 0 ? (
                <div className="p-10 text-center text-[#3A4557]"><FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />No revenue records yet. Import a file to get started.</div>
              ) : (
                <table className="w-full text-sm min-w-[1400px]" data-testid="records-table">
                  <thead className="bg-[#E2E9F4] text-[#3A4557] text-xs">
                    <tr>
                      <th className="p-2"><input type="checkbox" checked={selected.size === records.length && records.length > 0} onChange={toggleSelectAll} data-testid="select-all" /></th>
                      {["ID", "Channel", "Client", "Insurer", "Product", "Policy No", "Entry", "Yr", "Total Comm", "Source", "Associate", "Share %", "Referral", "Paid", "Net", "Status", "Notes", ""].map((h) => <th key={h} className="p-2 text-left whitespace-nowrap font-semibold">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r) => (
                      <tr key={r.id} className="border-t border-[#C9D4E6] hover:bg-white/60" data-testid={`rev-row-${r.record_id}`}>
                        <td className="p-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} /></td>
                        <td className="p-2 whitespace-nowrap font-mono text-xs text-[#3A4557]">{r.record_id}</td>
                        <td className="p-2 whitespace-nowrap">{r.channel}</td>
                        <td className="p-2 whitespace-nowrap">{r.client || "—"}</td>
                        <td className="p-2 whitespace-nowrap">{r.insurer || "—"}</td>
                        <td className="p-2 whitespace-nowrap max-w-[140px] truncate" title={r.product}>{r.product || "—"}</td>
                        <td className="p-2 whitespace-nowrap font-mono text-xs">{r.policy_no || "—"}</td>
                        <td className="p-2 whitespace-nowrap text-xs">{r.entry_type}</td>
                        <td className="p-2 text-center">{r.commission_year}</td>
                        <td className="p-2 whitespace-nowrap font-medium">{inr(r.total_commission)}</td>
                        <td className="p-2">
                          <Select value={r.source || "Direct"} onValueChange={(v) => patchRecord(r.id, { source: v })}>
                            <SelectTrigger className="w-28 h-8 bg-white text-xs" data-testid={`src-${r.record_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="p-2">
                          {r.source === "Associate" ? (
                            <Select value={r.associate || "none"} onValueChange={(v) => patchRecord(r.id, { associate: v === "none" ? "" : v })}>
                              <SelectTrigger className="w-32 h-8 bg-white text-xs" data-testid={`assoc-${r.record_id}`}><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent><SelectItem value="none">—</SelectItem>{associates.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : <span className="text-[#3A4557] text-xs">—</span>}
                        </td>
                        <td className="p-2">
                          <Input type="number" defaultValue={r.referral_share_pct ?? 0} onBlur={(e) => { const v = Number(e.target.value); if (v !== r.referral_share_pct) patchRecord(r.id, { referral_share_pct: v }); }} className="w-16 h-8 bg-white text-xs" data-testid={`share-${r.record_id}`} />
                        </td>
                        <td className="p-2 whitespace-nowrap text-amber-700">{inr(r.referral_amount)}</td>
                        <td className="p-2 text-center"><Switch checked={!!r.referral_paid} onCheckedChange={(v) => patchRecord(r.id, { referral_paid: v })} data-testid={`paid-${r.record_id}`} /></td>
                        <td className="p-2 whitespace-nowrap font-semibold text-emerald-700">{inr(r.net_revenue)}</td>
                        <td className="p-2">
                          <Select value={r.status || "CLEAN"} onValueChange={(v) => patchRecord(r.id, { status: v })}>
                            <SelectTrigger className="w-32 h-8 bg-white text-xs" data-testid={`status-${r.record_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="p-2"><Input defaultValue={r.notes || ""} onBlur={(e) => { if (e.target.value !== (r.notes || "")) patchRecord(r.id, { notes: e.target.value }); }} placeholder="…" className="w-32 h-8 bg-white text-xs" data-testid={`notes-${r.record_id}`} /></td>
                        <td className="p-2"><button onClick={() => deleteRecord(r.id)} className="text-red-500 hover:text-red-700" data-testid={`del-${r.record_id}`}><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <p className="text-[#3A4557] text-xs">Referral is deducted from net only when marked Paid; unpaid referral shows as payable in the summary. Editing Source/Associate/Share %/Paid/Notes never gets overwritten on re-import.</p>
          </TabsContent>

          {/* ---------------- FUTURE INCOME (PROJECTIONS) ---------------- */}
          <TabsContent value="projections" className="space-y-3">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <SubCard label="Expected future income" val={projSummary?.expected_total} color="text-blue-700" tid="proj-expected" icon={TrendingUp} />
              <SubCard label="Certain (contracted)" val={projSummary?.certain_total} color="text-emerald-700" tid="proj-certain" />
              <SubCard label="Probable (renewals)" val={projSummary?.probable_total} color="text-amber-700" tid="proj-probable" />
              <SubCard label="Overdue to collect" val={projSummary?.overdue_total} color="text-red-600" tid="proj-overdue" />
            </div>

            {projSummary?.by_fy?.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="proj-fy-breakdown">
                {projSummary.by_fy.map((f) => (
                  <div key={f.fy} className="bg-white/70 border border-[#C9D4E6] rounded-xl px-3 py-2 text-xs flex items-center gap-2">
                    <CalendarClock className="h-3.5 w-3.5 text-[#3A4557]" />
                    <span className="text-[#3A4557]">{f.fy}</span>
                    <span className="font-semibold text-[#1B2A4A]">{inr(f.amount)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2 items-center">
              <FilterSelect label="Certainty" value={projFilters.certainty} onChange={(v) => setProjFilters((f) => ({ ...f, certainty: v }))} options={["Certain", "Probable"]} tid="proj-filter-certainty" />
              <FilterSelect label="Channel" value={projFilters.channel} onChange={(v) => setProjFilters((f) => ({ ...f, channel: v }))} options={channels.map((c) => c.name)} tid="proj-filter-channel" />
              <FilterSelect label="Due" value={projFilters.overdue} onChange={(v) => setProjFilters((f) => ({ ...f, overdue: v }))} options={[{ v: "true", l: "Overdue only" }]} tid="proj-filter-overdue" />
            </div>

            <div className={`${cardCls} overflow-x-auto`}>
              {loadingProj ? (
                <div className="p-10 text-center text-[#3A4557]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
              ) : projections.length === 0 ? (
                <div className="p-10 text-center text-[#3A4557]"><CalendarClock className="h-8 w-8 mx-auto mb-2 opacity-50" />No upcoming income projected yet. Import multi-year or renewable policies to see future commission here.</div>
              ) : (
                <table className="w-full text-sm min-w-[1000px]" data-testid="projections-table">
                  <thead className="bg-[#E2E9F4] text-[#3A4557] text-xs">
                    <tr>{["Due date", "Type", "Client", "Policy No", "Insurer", "Channel", "Base amount", "Prob.", "Expected", "Bucket"].map((h) => <th key={h} className="p-2 text-left whitespace-nowrap font-semibold">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {projections.map((p) => (
                      <tr key={p.id} className={`border-t border-[#C9D4E6] hover:bg-white/60 ${p.overdue ? "bg-red-50/60" : ""}`} data-testid={`proj-row-${p.id}`}>
                        <td className="p-2 whitespace-nowrap">{p.due_date || "—"}{p.overdue && <Badge className="ml-1 bg-red-100 text-red-700 text-[10px] border-red-300">overdue</Badge>}</td>
                        <td className="p-2 whitespace-nowrap text-xs">{p.type}{p.type === "Multi-year payout" ? ` · Yr ${p.commission_year}` : ""}</td>
                        <td className="p-2 whitespace-nowrap">{p.client || "—"}</td>
                        <td className="p-2 whitespace-nowrap font-mono text-xs">{p.policy_no || "—"}</td>
                        <td className="p-2 whitespace-nowrap">{p.insurer || "—"}</td>
                        <td className="p-2 whitespace-nowrap">{p.channel}</td>
                        <td className="p-2 whitespace-nowrap">{inr(p.base_amount)}</td>
                        <td className="p-2 text-center whitespace-nowrap">{p.probability}%</td>
                        <td className="p-2 whitespace-nowrap font-semibold text-blue-700">{inr(p.expected_amount)}</td>
                        <td className="p-2"><Badge className={p.certainty === "Certain" ? "bg-emerald-100 text-emerald-700 text-[10px] border-emerald-300" : "bg-amber-100 text-amber-700 text-[10px] border-amber-300"}>{p.certainty}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <p className="text-[#3A4557] text-xs">Certain = future-year payouts already contracted on multi-year policies (100%). Probable = expected renewal commission (commissionable premium × the channel's renewal rate) weighted by the channel's renewal probability. Due dates are computed from each policy's start date + N years.</p>
          </TabsContent>

          {/* ---------------- IMPORT ---------------- */}
          <TabsContent value="import" className="space-y-4">
            <div className={`${cardCls} p-6`}>
              <input ref={fileRef} type="file" accept=".pdf,.csv" onChange={handleFile} className="hidden" data-testid="import-file-input" />
              <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-[#C9D4E6] rounded-xl p-8 text-center cursor-pointer hover:border-[#A87608] transition-colors" data-testid="import-dropzone">
                {uploading ? <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#A87608]" /> : <Upload className="h-8 w-8 mx-auto text-[#A87608]" />}
                <p className="text-[#1B2A4A] font-semibold mt-3">{uploading ? "Reading file…" : "Upload commission PDF or CSV"}</p>
                <p className="text-[#3A4557] text-sm mt-1">PDF is read with AI · CSV parsed directly. We dedupe on channel + policy + commission year.</p>
              </div>
            </div>

            {preview && (
              <div className={`${cardCls} p-5 space-y-4`} data-testid="import-preview">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge className="bg-slate-100 text-slate-700 border-slate-300">{preview.method === "ai" ? "AI extracted" : "CSV parsed"}</Badge>
                  <span className="text-[#1B2A4A] font-medium">{preview.filename}</span>
                  <div className="flex gap-2 ml-auto text-xs">
                    <Badge className={statusColor.new}>{preview.counts.new} new</Badge>
                    <Badge className={statusColor.updated}>{preview.counts.updated} updated</Badge>
                    <Badge className={statusColor.unchanged}>{preview.counts.unchanged} unchanged</Badge>
                    <Badge className={statusColor.failed}>{preview.counts.failed} failed</Badge>
                  </div>
                </div>

                {(preview.unknown_channels || []).length > 0 && (
                  <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 space-y-2" data-testid="unknown-channels">
                    <p className="text-amber-800 text-sm font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Unknown channels — map or create before committing:</p>
                    {preview.unknown_channels.map((u) => (
                      <div key={u} className="flex flex-wrap items-center gap-2">
                        <span className="text-[#1B2A4A] text-sm font-medium min-w-[140px]">{u}</span>
                        <Select value={channelMap[u] || ""} onValueChange={(v) => setChannelMap((m) => ({ ...m, [u]: v }))}>
                          <SelectTrigger className="w-64 bg-white h-9" data-testid={`map-${u}`}><SelectValue placeholder="Map to…" /></SelectTrigger>
                          <SelectContent>
                            {channels.map((c) => <SelectItem key={c.id} value={c.name}>→ {c.name}</SelectItem>)}
                            {CHANNEL_TYPES.map((t) => <SelectItem key={t} value={`__create__::${t}`}>➕ Create "{u}" as {t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}

                <div className="overflow-x-auto border border-[#C9D4E6] rounded-xl max-h-96">
                  <table className="w-full text-xs min-w-[900px]">
                    <thead className="bg-[#E2E9F4] text-[#3A4557] sticky top-0"><tr>{["Status", "ID", "Channel", "Client", "Insurer", "Policy No", "Entry", "Total"].map((h) => <th key={h} className="p-2 text-left whitespace-nowrap">{h}</th>)}</tr></thead>
                    <tbody>
                      {preview.rows.map((row, i) => (
                        <tr key={i} className="border-t border-[#C9D4E6]">
                          <td className="p-2"><Badge className={`${statusColor[row._status]} text-[10px]`}>{row._status}</Badge>{!row._channel_known && row.channel && <AlertTriangle className="h-3 w-3 inline ml-1 text-amber-500" />}</td>
                          <td className="p-2 font-mono">{row.record_id}</td>
                          <td className="p-2 whitespace-nowrap">{row.channel}</td>
                          <td className="p-2 whitespace-nowrap">{row.client || "—"}</td>
                          <td className="p-2 whitespace-nowrap">{row.insurer || "—"}</td>
                          <td className="p-2 font-mono">{row.policy_no || "—"}</td>
                          <td className="p-2 whitespace-nowrap">{row.entry_type}</td>
                          <td className="p-2 whitespace-nowrap">{inr(row.total_commission)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setPreview(null)} className="border-[#C9D4E6]">Cancel</Button>
                  <Button onClick={commitImport} disabled={committing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="import-commit-btn">
                    {committing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}Commit import
                  </Button>
                </div>
              </div>
            )}

            {imports.length > 0 && (
              <div className={`${cardCls} p-4`}>
                <p className="text-[#1B2A4A] font-semibold text-sm flex items-center gap-2 mb-2"><History className="h-4 w-4" /> Import log</p>
                <div className="space-y-1 text-xs text-[#3A4557]">
                  {imports.map((im) => (
                    <div key={im.id} className="flex flex-wrap gap-2 items-center border-b border-[#C9D4E6]/50 py-1">
                      <span className="font-medium text-[#1B2A4A]">{im.filename}</span>
                      <Badge className="bg-slate-100 text-slate-600 text-[10px]">{im.method === "ai" ? "AI" : "CSV"}</Badge>
                      <span>{new Date(im.imported_at).toLocaleString("en-IN")}</span>
                      <span className="ml-auto">{im.results?.new || 0} new · {im.results?.updated || 0} updated · {im.rows_processed} rows</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ---------------- CHANNELS ---------------- */}
          <TabsContent value="channels" className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-[#1B2A4A] font-semibold flex items-center gap-2"><Building2 className="h-4 w-4" /> Channels</p>
              <Button onClick={() => setChanForm({ type: "Broker platform", default_renewal_rate_pct: 7.5, default_renewal_probability: 70 })} className="bg-[#A87608] hover:bg-[#8f6307] text-white h-9" data-testid="add-channel-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
            <div className={`${cardCls} overflow-x-auto`}>
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-[#E2E9F4] text-[#3A4557] text-xs"><tr>{["Name", "Type", "Payout entity", "Renewal %", "Renewal prob %", "Active", ""].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
                <tbody>
                  {channels.map((c) => (
                    <tr key={c.id} className="border-t border-[#C9D4E6]" data-testid={`channel-row-${c.name}`}>
                      <td className="p-2 font-medium text-[#1B2A4A]">{c.name}</td>
                      <td className="p-2">{c.type}</td>
                      <td className="p-2">{c.payout_entity || "—"}</td>
                      <td className="p-2">{c.default_renewal_rate_pct}%</td>
                      <td className="p-2">{c.default_renewal_probability}%</td>
                      <td className="p-2">{c.active ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge> : <Badge className="bg-slate-100 text-slate-500">Off</Badge>}</td>
                      <td className="p-2 flex gap-2">
                        <button onClick={() => setChanForm({ ...c })} className="text-[#A87608] text-xs hover:underline">Edit</button>
                        <button onClick={() => deleteChannel(c.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ---------------- ASSOCIATES ---------------- */}
          <TabsContent value="associates" className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-[#1B2A4A] font-semibold flex items-center gap-2"><Handshake className="h-4 w-4" /> Associates</p>
              <Button onClick={() => setAssocForm({ default_share_pct: 0 })} className="bg-[#A87608] hover:bg-[#8f6307] text-white h-9" data-testid="add-associate-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
            <div className={`${cardCls} overflow-x-auto`}>
              <table className="w-full text-sm min-w-[520px]">
                <thead className="bg-[#E2E9F4] text-[#3A4557] text-xs"><tr>{["Name", "Default share %", "Contact", "Active", ""].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
                <tbody>
                  {associates.length === 0 ? <tr><td colSpan={5} className="p-6 text-center text-[#3A4557]">No associates yet.</td></tr> : associates.map((a) => (
                    <tr key={a.id} className="border-t border-[#C9D4E6]" data-testid={`associate-row-${a.name}`}>
                      <td className="p-2 font-medium text-[#1B2A4A]">{a.name}</td>
                      <td className="p-2">{a.default_share_pct}%</td>
                      <td className="p-2">{a.contact || "—"}</td>
                      <td className="p-2">{a.active ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge> : <Badge className="bg-slate-100 text-slate-500">Off</Badge>}</td>
                      <td className="p-2 flex gap-2">
                        <button onClick={() => setAssocForm({ ...a })} className="text-[#A87608] text-xs hover:underline">Edit</button>
                        <button onClick={() => deleteAssociate(a.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Channel dialog */}
      <Dialog open={!!chanForm} onOpenChange={(o) => !o && setChanForm(null)}>
        <DialogContent className="bg-white" data-testid="channel-dialog">
          <DialogHeader><DialogTitle>{chanForm?.id ? "Edit" : "Add"} Channel</DialogTitle></DialogHeader>
          {chanForm && (
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input value={chanForm.name || ""} onChange={(e) => setChanForm((f) => ({ ...f, name: e.target.value }))} data-testid="channel-name" /></div>
              <div><Label className="text-xs">Type</Label>
                <Select value={chanForm.type} onValueChange={(v) => setChanForm((f) => ({ ...f, type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CHANNEL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Payout entity (as in bank statement)</Label><Input value={chanForm.payout_entity || ""} onChange={(e) => setChanForm((f) => ({ ...f, payout_entity: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Renewal rate %</Label><Input type="number" value={chanForm.default_renewal_rate_pct} onChange={(e) => setChanForm((f) => ({ ...f, default_renewal_rate_pct: e.target.value }))} /></div>
                <div><Label className="text-xs">Renewal probability %</Label><Input type="number" value={chanForm.default_renewal_probability} onChange={(e) => setChanForm((f) => ({ ...f, default_renewal_probability: e.target.value }))} /></div>
              </div>
              <Button onClick={saveChannel} className="w-full bg-[#A87608] hover:bg-[#8f6307] text-white" data-testid="channel-save"><Save className="h-4 w-4 mr-2" />Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Associate dialog */}
      <Dialog open={!!assocForm} onOpenChange={(o) => !o && setAssocForm(null)}>
        <DialogContent className="bg-white" data-testid="associate-dialog">
          <DialogHeader><DialogTitle>{assocForm?.id ? "Edit" : "Add"} Associate</DialogTitle></DialogHeader>
          {assocForm && (
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input value={assocForm.name || ""} onChange={(e) => setAssocForm((f) => ({ ...f, name: e.target.value }))} data-testid="associate-name-input" /></div>
              <div><Label className="text-xs">Default share %</Label><Input type="number" value={assocForm.default_share_pct} onChange={(e) => setAssocForm((f) => ({ ...f, default_share_pct: e.target.value }))} /></div>
              <div><Label className="text-xs">Contact</Label><Input value={assocForm.contact || ""} onChange={(e) => setAssocForm((f) => ({ ...f, contact: e.target.value }))} /></div>
              <Button onClick={saveAssociate} className="w-full bg-[#A87608] hover:bg-[#8f6307] text-white" data-testid="associate-save"><Save className="h-4 w-4 mr-2" />Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <MobileBottomNav />
    </div>
  );
};

const SubCard = ({ label, val, color, tid, icon: Icon }) => (
  <div className="bg-white/60 border border-[#C9D4E6] rounded-2xl p-4" data-testid={tid}>
    <p className="text-[#3A4557] text-xs flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</p>
    <p className={`text-xl font-bold mt-1 ${color}`}>{val === null || val === undefined ? "—" : "₹" + Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</p>
  </div>
);

const FilterSelect = ({ label, value, onChange, options, tid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-auto min-w-[120px] bg-white h-9 text-sm" data-testid={tid}><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All {label}</SelectItem>
      {options.map((o) => (typeof o === "string" ? <SelectItem key={o} value={o}>{o}</SelectItem> : <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>))}
    </SelectContent>
  </Select>
);

export default RevenueDashboard;
